import { memo } from "react";
import { Checkbox } from "../ui/checkbox";
import { Globe, PhoneCall } from "@phosphor-icons/react";
import { responseText } from "./LeadCells";
import { AssignedInlineCell, StatusInlineCell, CardAssignedLine } from "./LeadInlineCells";
import { StatusPill } from "../lux/Primitives";
import { LeadPrimary, LeadActionsMenu, websitePrimaryKind, showConvertInMenu, LEAD_STATUS_TONE } from "./leadActions";
import { formatPhone, telHref } from "../../lib/phone";

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
const respLine = (l) => {
  if (!l.first_contacted_at) return { text: "No reply yet", warn: true };
  const rt = responseText(l);
  return { text: rt === "—" ? "—" : `Replied in ${rt}`, warn: false };
};

const WebsiteRow = memo(function WebsiteRow({
  l, team, selecting, selected, highlight, editingField, isAdmin,
  onStartEdit, onCancelEdit, onCommit, toggleRow,
  onConvert, onEdit, onDelete, onOpenBooking,
}) {
  const status = (l.status || "new").toLowerCase();
  const kind = websitePrimaryKind(l);
  const resp = respLine(l);
  const phoneHref = telHref(l.customer_phone);
  const dates = `${fmtDate(l.pickup_date)} → ${fmtDate(l.return_date)}`;
  return (
    <tr className={`border-b border-border/40 last:border-b-0 transition-colors duration-500 ${highlight ? "bg-primary/10" : "hover:bg-white/[0.03]"}`} data-testid={`website-lead-row-${l.id}`}>
      {selecting && (
        <td className="px-3 py-3 align-top">
          <Checkbox checked={selected} onCheckedChange={() => toggleRow(l.id)} data-testid={`website-lead-select-${l.id}`} />
        </td>
      )}
      <td className="px-3 py-3">
        <div className="font-heading text-[15px] truncate" title={l.customer_name}>{l.customer_name}</div>
        <div className="text-xs text-muted-foreground capitalize truncate">{l.service_type}{l.vehicle_preference ? ` · ${l.vehicle_preference}` : ""}</div>
      </td>
      <td className="px-3 py-3">
        {l.customer_phone
          ? (phoneHref ? <a href={phoneHref} className="whitespace-nowrap tabular-nums text-[13px] text-foreground hover:text-primary hover:underline" data-testid={`website-lead-phone-${l.id}`}>{formatPhone(l.customer_phone)}</a> : <span className="whitespace-nowrap tabular-nums text-[13px]">{formatPhone(l.customer_phone)}</span>)
          : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="pl-3 pr-1 py-3" data-testid={`website-lead-salesperson-${l.id}`}>
        <AssignedInlineCell
          lead={l} team={team} editing={editingField === "assigned"}
          onStartEdit={() => onStartEdit(l.id, "assigned")} onCancelEdit={onCancelEdit}
          onCommit={(v) => onCommit(l.id, "assigned", v)}
        />
      </td>
      <td className="px-3 py-3 whitespace-nowrap">
        <div className="text-[13px] tabular-nums text-muted-foreground" title={dates}>{dates}</div>
        <div className={`text-xs ${resp.warn ? "text-amber-400" : "text-muted-foreground"}`} data-testid={`website-lead-response-${l.id}`}>{resp.text}</div>
      </td>
      <td className="px-3 py-3">
        <StatusInlineCell
          lead={l} editing={editingField === "status"}
          onStartEdit={() => onStartEdit(l.id, "status")} onCancelEdit={onCancelEdit}
          onCommit={(v) => onCommit(l.id, "status", v)}
        />
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center justify-end gap-2">
          <LeadPrimary lead={l} kind={kind} idp="website-lead" onConvert={onConvert} onOpenBooking={onOpenBooking} />
          <LeadActionsMenu
            lead={l} idp="website-lead" isAdmin={isAdmin} showConvert={showConvertInMenu(l, kind)}
            onEdit={onEdit} onConvert={onConvert} onDelete={onDelete}
          />
        </div>
      </td>
    </tr>
  );
});

const WebsiteCard = memo(function WebsiteCard({
  l, selecting, selected, highlight, isAdmin, toggleRow,
  onConvert, onEdit, onDelete, onOpenBooking,
}) {
  const status = (l.status || "new").toLowerCase();
  const kind = websitePrimaryKind(l);
  const phoneHref = telHref(l.customer_phone);
  return (
    <div className={`rounded-2xl border p-4 flex flex-col gap-3 transition-colors duration-500 ${highlight ? "border-primary/40 bg-primary/10" : "border-border bg-card"}`} data-testid={`website-lead-card-${l.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          {selecting && <div className="hidden md:block pt-1"><Checkbox checked={selected} onCheckedChange={() => toggleRow(l.id)} data-testid={`website-lead-card-select-${l.id}`} /></div>}
          <div className="min-w-0">
            <div className="font-heading text-base truncate" title={l.customer_name}>{l.customer_name}</div>
            <div className="text-xs text-muted-foreground truncate capitalize">
              {l.service_type}{l.vehicle_preference ? ` · ${l.vehicle_preference}` : ""}
            </div>
          </div>
        </div>
        <StatusPill status={status} tone={LEAD_STATUS_TONE[status]} className="shrink-0" />
      </div>
      <div className="flex items-center justify-between gap-3 text-sm">
        {l.customer_phone && phoneHref
          ? <a href={phoneHref} className="inline-flex shrink-0 min-h-[44px] items-center gap-1.5 text-primary" data-testid={`website-lead-phone-${l.id}`}><PhoneCall size={14} />{formatPhone(l.customer_phone)}</a>
          : <span className="shrink-0 text-muted-foreground">{formatPhone(l.customer_phone) || "—"}</span>}
        <div className="flex flex-col items-end min-w-0">
          <CardAssignedLine lead={l} testid={`website-lead-card-assigned-${l.id}`} />
          <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(l.pickup_date)} → {fmtDate(l.return_date)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <LeadPrimary lead={l} kind={kind} idp="website-lead" touch onConvert={onConvert} onOpenBooking={onOpenBooking} />
        <LeadActionsMenu lead={l} idp="website-lead" isAdmin={isAdmin} touch showConvert={showConvertInMenu(l, kind)} onEdit={onEdit} onConvert={onConvert} onDelete={onDelete} />
      </div>
    </div>
  );
});

export const WebsiteLeadsTable = memo(function WebsiteLeadsTable({
  rows, loading, team, selecting, selected, toggleRow, isAdmin,
  editingCell, onStartEdit, onCancelEdit, onCommit,
  onConvert, onEdit, onDelete, onOpenBooking, highlightId,
}) {
  const empty = loading ? "Loading…" : rows.length === 0 ? "empty" : null;
  return (
    <div className="rounded-2xl border border-emerald-500/35 bg-emerald-500/[0.04] p-5" data-testid="website-leads-section">
      <div className="flex items-center gap-2.5 mb-4">
        <Globe size={17} className="text-emerald-400" />
        <h3 className="font-heading text-lg text-emerald-300">Website Leads</h3>
        <span className="tnum text-sm text-muted-foreground" data-testid="website-leads-count">{rows.length}</span>
        <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">— contacted website requests. Convert once everything is collected.</span>
      </div>

      {/* Desktop table (xl and up) */}
      <div className="hidden xl:block border border-border bg-card overflow-hidden rounded-xl">
        <table className="w-full text-sm table-fixed">
          <thead>
            <tr className="border-b border-border">
              {selecting && <th className="w-10 px-3 py-3" />}
              <th className="text-left px-3 py-3 label-mono font-normal">Lead</th>
              <th className="text-left px-3 py-3 label-mono font-normal w-[136px]">Phone</th>
              <th className="text-left px-3 py-3 label-mono font-normal w-[124px]">Assigned</th>
              <th className="text-left px-3 py-3 label-mono font-normal w-[136px]">Dates</th>
              <th className="text-left px-3 py-3 label-mono font-normal w-[142px]">Status</th>
              <th className="text-right px-3 py-3 label-mono font-normal w-[200px]">Next step</th>
            </tr>
          </thead>
          <tbody>
            {empty === "Loading…" ? <tr><td colSpan={selecting ? 7 : 6} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
              : empty === "empty" ? (
                <tr><td colSpan={selecting ? 7 : 6} className="p-8 text-center text-muted-foreground text-sm">
                  Nothing here yet. Website requests land in <b>Submitted Bookings ▸ Website Bookings</b> — hit “Contacted ✓” there and they move into this section.
                </td></tr>
              ) : rows.map((l) => (
                <WebsiteRow
                  key={l.id} l={l} team={team} isAdmin={isAdmin}
                  selecting={selecting}
                  selected={selected.includes(l.id)}
                  highlight={highlightId === l.id}
                  editingField={editingCell?.id === l.id ? editingCell.field : null}
                  onStartEdit={onStartEdit} onCancelEdit={onCancelEdit} onCommit={onCommit}
                  toggleRow={toggleRow}
                  onConvert={onConvert} onEdit={onEdit} onDelete={onDelete} onOpenBooking={onOpenBooking}
                />
              ))}
          </tbody>
        </table>
      </div>

      {/* Phone + tablet cards (below xl) */}
      <div className="xl:hidden">
        {empty === "Loading…" ? <div className="border border-border bg-card rounded-2xl p-8 text-center text-muted-foreground">Loading…</div>
          : empty === "empty" ? (
            <div className="border border-border bg-card rounded-2xl p-8 text-center text-muted-foreground text-sm">
              Nothing here yet. Website requests land in <b>Submitted Bookings ▸ Website Bookings</b>.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {rows.map((l) => (
                <WebsiteCard
                  key={l.id} l={l} isAdmin={isAdmin}
                  selecting={selecting}
                  selected={selected.includes(l.id)}
                  highlight={highlightId === l.id}
                  toggleRow={toggleRow}
                  onConvert={onConvert} onEdit={onEdit} onDelete={onDelete} onOpenBooking={onOpenBooking}
                />
              ))}
            </div>
          )}
      </div>
    </div>
  );
});
